import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, CheckCircle2, Users, Cpu, HeartHandshake,
  Clock, Shield, Star, Network, Search, ClipboardList,
  Cog, MapPin, PackageCheck
} from 'lucide-react';
import Hero from '../components/Hero';
import './About.css';

const About = () => {
  const strengths = [
    { icon: CheckCircle2, title: '100% Customer-Focused Logistics Solutions', desc: 'We prioritize our customers and design solutions that deliver real value.' },
    { icon: Users, title: 'Experienced Leadership Team', desc: 'Professionally managed by a leadership team with deep industry expertise.' },
    { icon: Cpu, title: 'State-of-the-Art Logistics Technology', desc: 'We leverage advanced technology for better visibility and operational efficiency.' },
    { icon: HeartHandshake, title: 'Advanced CRM System', desc: 'Our CRM system ensures seamless communication and total transparency.' },
    { icon: Star, title: 'Dedicated Logistics Professionals', desc: 'Highly motivated and skilled team committed to your success.' },
    { icon: Network, title: 'Strong Global Network', desc: 'Extensive network of trusted partners and agents across the world.' },
    { icon: Shield, title: 'Reliable & Cost-Effective', desc: 'Honest, transparent, and cost-effective solutions you can rely on.' },
    { icon: Clock, title: 'On-Time Delivery', desc: 'We are committed to on-time delivery and long-term customer satisfaction.' },
  ];

  const process = [
    { icon: Search, title: 'Understand', desc: 'We listen to your requirements' },
    { icon: ClipboardList, title: 'Plan', desc: 'Custom logistics strategy' },
    { icon: Cog, title: 'Execute', desc: 'Precise, professional execution' },
    { icon: MapPin, title: 'Track', desc: 'Real-time visibility & updates' },
    { icon: PackageCheck, title: 'Deliver', desc: 'On-time, every time' },
  ];

  return (
    <div className="about">
      <Hero
        label="About Us"
        title="About Denver Logistics"
        subtitle="Reliable logistics. Professional execution. Customer-focused solutions."
        size="medium"
        image="/img/about-hero.jpg"
      />

      {/* Who We Are */}
      <section className="section about-intro">
        <div className="container">
          <div className="about-intro__grid">
            <div className="about-intro__content">
              <span className="section-label">Who We Are</span>
              <h2 className="section-title">
                Delivering Reliable Logistics.<br />
                Building Stronger Partnerships.
              </h2>
              <p>
                Denver Logistics Private Limited is one of the emerging and trusted logistics
                and freight forwarding companies, committed to delivering reliable, efficient,
                and cost-effective supply chain solutions.
              </p>
              <p>
                We provide comprehensive multimodal transportation services to customers across
                diverse industries through our extensive network of global partners, agents,
                and strategic alliances.
              </p>
              <p>
                Our integrated logistics solutions cover air freight, sea freight, road transportation,
                customs clearance, warehousing, and end-to-end supply chain management, ensuring every
                shipment is handled with precision and professionalism.
              </p>
            </div>
            <div className="about-intro__visual">
              <div className="about-intro__card about-intro__card--1">
                <h3>Global Reach</h3>
                <p>Connecting businesses across key global trade lanes</p>
              </div>
              <div className="about-intro__card about-intro__card--2">
                <h3>End-to-End</h3>
                <p>From origin to destination, we manage every detail</p>
              </div>
              <div className="about-intro__card about-intro__card--3">
                <h3>Customer First</h3>
                <p>Your success is our priority, always</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Strengths */}
      <section className="section about-strengths">
        <div className="container">
          <div className="text-center">
            <span className="section-label">Our Strengths</span>
            <h2 className="section-title">Why Denver Logistics?</h2>
          </div>
          <div className="about-strengths__grid">
            {strengths.map((item, index) => (
              <div key={index} className="about-strength-card">
                <div className="about-strength-card__icon">
                  <item.icon size={24} />
                </div>
                <div>
                  <h4 className="about-strength-card__title">{item.title}</h4>
                  <p className="about-strength-card__desc">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="section about-process">
        <div className="container">
          <div className="text-center">
            <span className="section-label">Our Approach</span>
            <h2 className="section-title">How We Work</h2>
            <p className="section-subtitle">
              A structured, transparent approach to ensure your cargo reaches its destination safely and on time.
            </p>
          </div>
          <div className="about-process__timeline">
            {process.map((step, index) => (
              <div key={index} className="process-step">
                <div className="process-step__icon">
                  <step.icon size={28} />
                </div>
                <h4 className="process-step__title">{step.title}</h4>
                <p className="process-step__desc">{step.desc}</p>
                {index < process.length - 1 && <div className="process-step__connector" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section about-cta">
        <div className="container">
          <div className="about-cta__inner">
            <h2>Let's Move Forward Together</h2>
            <p>Partner with Denver Logistics for reliable, efficient, and customized supply chain solutions.</p>
            <div className="about-cta__actions">
              <Link to="/quote" className="btn btn-white btn-lg">
                Get a Quote <ArrowRight size={18} />
              </Link>
              <Link to="/contact" className="btn btn-secondary btn-lg">
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
